import { useEffect, useState, type ReactNode } from "react"

import { formatAppDateTime, formatShortDate, formatShortDateRange } from "../shared/dateUtils"
import closeIconUrl from "./icons/close.svg"
import type { StayBookingSummary, StayBookingSummaryRow } from "./stayData"

type BookingSummaryModalProps = {
  summaryUrl: string
}

export function BookingSummaryModal({ summaryUrl }: BookingSummaryModalProps) {
  const [open, setOpen] = useState(false)
  const [summary, setSummary] = useState<StayBookingSummary | null>(null)

  useEffect(() => {
    if (!open) return

    const previousOverflow = document.body.style.overflow
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false)
      }
    }

    document.body.style.overflow = "hidden"
    document.addEventListener("keydown", handleKeyDown)

    return () => {
      document.body.style.overflow = previousOverflow
      document.removeEventListener("keydown", handleKeyDown)
    }
  }, [open])

  function openSummary() {
    if (summary) {
      setOpen(true)
      return
    }

    void fetch(summaryUrl, { headers: { Accept: "application/json" } })
      .then((response) => response.json() as Promise<StayBookingSummary>)
      .then((data) => {
        setSummary(data)
        setOpen(true)
      })
  }

  return (
    <>
      <button
        aria-haspopup="dialog"
        className="inline-flex h-7 shrink-0 items-center gap-1.5 rounded border border-[#d5d5d5] bg-white px-2.5 text-[11px] font-medium leading-4 text-[#424242] hover:bg-[#f2f2f2] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#337ab7] focus-visible:ring-offset-2"
        onClick={openSummary}
        type="button"
      >
        <SummaryIcon />
        <span>Booking Summary</span>
      </button>

      {open && summary ? (
        <div className="fixed inset-0 z-50 grid place-items-center px-4 py-8">
          <div aria-hidden="true" className="absolute inset-0 bg-black/35" onClick={() => setOpen(false)} />

          <section
            aria-labelledby="booking-summary-modal-title"
            aria-modal="true"
            className="relative z-10 flex max-h-[calc(100vh-64px)] w-full max-w-6xl flex-col overflow-hidden rounded-[6px] border border-[#d5d5d5] bg-white shadow-[0_20px_60px_rgba(34,34,34,0.24)]"
            role="dialog"
          >
            <header className="flex items-start justify-between gap-4 border-b border-[#d5d5d5] px-6 py-5">
              <div className="min-w-0">
                <p className="text-[11px] font-bold uppercase tracking-[0.66px] text-[#7e7e7e]">
                  Booking Summary
                </p>
                <h2 className="mt-1 text-xl font-bold leading-7 text-[#2b2b2b]" id="booking-summary-modal-title">
                  {summary.title}
                </h2>
                <p className="mt-1 text-sm font-medium leading-5 text-[#687482]">
                  Services linked to this vacation
                </p>
              </div>
              <button
                autoFocus
                aria-label="Close booking summary"
                className="grid size-9 shrink-0 place-items-center rounded text-2xl leading-none text-[#687482] hover:bg-[#f2f2f2] hover:text-[#2b2b2b] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#337ab7]"
                onClick={() => setOpen(false)}
                type="button"
              >
                <img alt="" className="block size-4" src={closeIconUrl} />
              </button>
            </header>

            <div className="min-h-0 flex-1 overflow-auto px-6 py-5">
              <section className="rounded-[6px] border border-[#d5d5d5] bg-white px-5 py-5">
                <h3 className="text-base font-bold leading-6 text-[#424242]">Invoiced itinerary</h3>

                {summary.rows.length ? (
                  <div className="mt-4 overflow-x-auto">
                    <table className="w-full min-w-[900px] border-collapse text-left">
                      <thead>
                        <tr>
                          <SummaryHeaderCell>Service Date</SummaryHeaderCell>
                          <SummaryHeaderCell>Supplier</SummaryHeaderCell>
                          <SummaryHeaderCell>Type</SummaryHeaderCell>
                          <SummaryHeaderCell>Service</SummaryHeaderCell>
                          <SummaryHeaderCell>Booking Reference</SummaryHeaderCell>
                          <SummaryHeaderCell>Booking Status</SummaryHeaderCell>
                          <SummaryHeaderCell>Latest Event</SummaryHeaderCell>
                        </tr>
                      </thead>
                      <tbody>
                        {summary.rows.map((row) => (
                          <BookingSummaryRow row={row} key={row.id} />
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="mt-4 rounded border border-dashed border-[#cfcfcf] bg-[#f7f7f7] px-4 py-3 text-sm font-medium leading-5 text-[#817d76]">
                    No booking summary items available for this vacation.
                  </p>
                )}
              </section>
            </div>

            <footer className="flex justify-end border-t border-[#d5d5d5] px-6 py-4">
              <button
                className="rounded border border-[#d5d5d5] bg-white px-4 py-2 text-sm font-bold leading-5 text-[#424242] hover:bg-[#f2f2f2] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#337ab7] focus-visible:ring-offset-2"
                onClick={() => setOpen(false)}
                type="button"
              >
                Close
              </button>
            </footer>
          </section>
        </div>
      ) : null}
    </>
  )
}

function BookingSummaryRow({ row }: { row: StayBookingSummaryRow }) {
  const serviceDate = row.end_date ? formatShortDateRange(row.start_date, row.end_date) : formatShortDate(row.start_date)
  const latestEvent = row.latest_event
  const latestEventDescription = latestEvent?.description?.trim() || "-"
  const latestEventCreatedByName = latestEvent?.created_by_name?.trim()
  const latestEventCreatedAt = latestEvent?.created_at?.trim()
  return (
    <tr className="border-b border-[#e2e2e2] last:border-b-0">
      <SummaryCell>{formatShortDate(row.start_date)}</SummaryCell>
      <SummaryCell>{row.supplier_name || "-"}</SummaryCell>
      <SummaryCell>
        <span className="inline-flex items-center gap-2 whitespace-nowrap">
          <span aria-hidden="true" className="size-2 rounded-full bg-[#c2d3ea]" />
          {row.supplier_service_type_label}
        </span>
      </SummaryCell>
      <SummaryCell>
        {row.supplier_service_type_label === "Accommodation" ? (
          <>
            {row.service_description} ({serviceDate})
          </>
        ) : (
          row.service_description
        )}
      </SummaryCell>
      <SummaryCell>{row.booking_reference || "-"}</SummaryCell>
      <SummaryCell>
        <span className="capitalize">{row.booking_status}</span>
      </SummaryCell>
      <SummaryCell>
        {latestEvent ? (
          <span className="grid gap-1">
            <span>{latestEventDescription}</span>
            {latestEventCreatedByName || latestEventCreatedAt ? (
              <span className="text-xs font-medium leading-4 text-[#817d76]">
                {latestEventCreatedByName ? <span>{latestEventCreatedByName}</span> : null}
                {latestEventCreatedByName && latestEventCreatedAt ? <span aria-hidden="true"> - </span> : null}
                {latestEventCreatedAt ? formatAppDateTime(latestEventCreatedAt) : null}
              </span>
            ) : null}
          </span>
        ) : (
          "-"
        )}
      </SummaryCell>
    </tr>
  )
}

function SummaryHeaderCell({ children }: { children: string }) {
  return (
    <th className="border-b border-[#e2e2e2] px-3 py-3 text-xs font-bold uppercase tracking-[0.48px] text-[#7e7e7e]">
      {children}
    </th>
  )
}

function SummaryCell({ children }: { children: ReactNode }) {
  return <td className="px-3 py-4 align-top text-sm font-medium leading-5 text-[#4a4a4a]">{children}</td>
}

function SummaryIcon() {
  return (
    <svg aria-hidden="true" className="size-3.5" fill="none" viewBox="0 0 16 16">
      <path d="M4.25 4.5h7.5" stroke="currentColor" strokeLinecap="round" strokeWidth="1.6" />
      <path d="M4.25 8h7.5" stroke="currentColor" strokeLinecap="round" strokeWidth="1.6" />
      <path d="M4.25 11.5h5" stroke="currentColor" strokeLinecap="round" strokeWidth="1.6" />
      <path
        d="M2.25 4.5h.01M2.25 8h.01M2.25 11.5h.01"
        stroke="currentColor"
        strokeLinecap="round"
        strokeWidth="2"
      />
    </svg>
  )
}
